"use client"

import { useQuery } from "@tanstack/react-query"

import { getProductAsLowAs } from "@lib/data"

interface AsLowAsData {
  installments: string
  amount: string
}

/**
 * Custom hook that fetches product pricing in installments format
 *
 * @param finalPrice - The product price to calculate installments for
 * @param customerExternalId - Optional customer ID for personalized pricing
 * @returns A TanStack Query result object containing installment data and loading state
 */
export const useProductAsLowAs = (finalPrice: string, customerExternalId?: string | null) => {
  const sanitizedFinalPrice = finalPrice.replace(/[^0-9.-]+/g, "")

  return useQuery({
    queryKey: ["productAsLowAs", sanitizedFinalPrice, customerExternalId],
    queryFn: async (): Promise<AsLowAsData> => {
      try {
        const data = await getProductAsLowAs(customerExternalId ?? null, sanitizedFinalPrice)
        return {
          installments: data.installments,
          amount: data.amount,
        }
      } catch (error) {
        console.error("Error fetching product as-low-as data:", error)
        throw error
      }
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    placeholderData: { installments: "", amount: "" },
  })
}
